import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, User, MessageSquare, Save, Trash2, DollarSign, Unlock, XCircle, RotateCcw, FileText, Send, Eye, Download, ClipboardCheck, Plus, X, FilePlus2, Receipt, ChevronDown, CreditCard, ExternalLink, Upload, Paperclip, Pencil } from "lucide-react";
import { formatET, formatDateET, todayET } from "@/lib/dates";

const STATUSES = ["new", "contacted", "tour_scheduled", "tour_complete", "contract_sent", "contract_signed", "paid", "cancelled", "refunded", "lost"];
const STATUS_LABELS = { new: "New", contacted: "Contacted", tour_scheduled: "Tour Scheduled", tour_complete: "Tour Complete", contract_sent: "Contract Sent", contract_signed: "Contract Signed", paid: "Paid", cancelled: "Cancelled", refunded: "Refunded", lost: "Lost" };
const NOTE_TAGS = ["call", "email", "text", "in-person", "voicemail"];
const TAG_COLORS = { call: "bg-blue-500/20 text-blue-400", email: "bg-emerald-500/20 text-emerald-400", text: "bg-purple-500/20 text-purple-400", "in-person": "bg-amber-500/20 text-amber-400", voicemail: "bg-rose-500/20 text-rose-400" };

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandUsers, setBrandUsers] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [noteTag, setNoteTag] = useState("call");
  const [passcodeDialog, setPasscodeDialog] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [showContract, setShowContract] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractForm, setContractForm] = useState({ template_id: "", waiver_id: "", deposit_amount: "", total_amount: "", payment_schedule: "", custom_terms: "", field_values: {}, recurrence: null });
  const [recurrenceOn, setRecurrenceOn] = useState(false);
  // schedule_mode: "single" | "recurring" | "custom"
  const [scheduleMode, setScheduleMode] = useState("single");
  const [recForm, setRecForm] = useState({ weekdays: [], start_date: "", weeks: 4, start_time: "18:00", end_time: "20:00", skip_holidays: true });
  const [customDates, setCustomDates] = useState([]); // list of {date, start_time, end_time, hours}
  const [recPreview, setRecPreview] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const selectedTemplate = templates.find((t) => t.id === contractForm.template_id);
  const templateFields = selectedTemplate?.fields || [];
  const [contractPreview, setContractPreview] = useState("");
  const [viewContract, setViewContract] = useState(null);
  const [showEmailer, setShowEmailer] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", body: "", cc: "" });
  const [emails, setEmails] = useState([]);
  const [showBookingInfo, setShowBookingInfo] = useState(false);
  const [finances, setFinances] = useState({ total_due: 0, paid: 0, balance: 0, is_paid_in_full: false, contract_count: 0 });
  const [payments, setPayments] = useState([]);
  const [showAddendum, setShowAddendum] = useState(false);
  const [addendumForm, setAddendumForm] = useState({ title: "Contract Addendum", notes: "", new_event_date: "", line_items: [{ description: "", qty: 1, unit_price: 0 }] });
  const [showPayment, setShowPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: "", method: "check", received_at: todayET(), note: "" });
  const [related, setRelated] = useState({ current: [], past: [] });
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [newBookingForm, setNewBookingForm] = useState({ inquiry_type: "rental", location: "rrdc_field", requested_date: "", start_time: "", duration: "" });
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [showDuplicatePicker, setShowDuplicatePicker] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ file: null, label: "", is_signed_contract: false });
  const [uploading, setUploading] = useState(false);
  const [showEditLead, setShowEditLead] = useState(false);
  const [editLeadForm, setEditLeadForm] = useState({ first_name: "", last_name: "", email: "", phone: "", organization: "", street: "", city: "", state: "", zip_code: "" });
  const [savingLeadEdit, setSavingLeadEdit] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [waiverForm, setWaiverForm] = useState({ waiver_id: "", renter_first_name: "", renter_last_name: "", renter_email: "", renter_phone: "", renter_organization: "", renter_address: "", update_lead: false });
  const [sendingWaiver, setSendingWaiver] = useState(false);

  useEffect(() => {
    api.get(`/leads/${id}`)
      .then(({ data }) => {
        setLead(data.lead);
        setNotes(data.notes);
        if (data.lead.brand) {
          api.get(`/users/brand/${data.lead.brand}`).then(({ data: ud }) => setBrandUsers(ud.users)).catch(() => {});
        }
      })
      .catch(() => toast.error("Lead not found"))
      .finally(() => setLoading(false));
    api.get("/contract-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
    api.get(`/leads/${id}/contracts`).then(({ data }) => setContracts(data.contracts)).catch(() => {});
    api.get(`/leads/${id}/emails`).then(({ data }) => setEmails(data.emails)).catch(() => {});
    api.get(`/leads/${id}/related`).then(({ data }) => setRelated({ current: data.current || [], past: data.past || [] })).catch(() => {});
    api.get(`/leads/${id}/contract-draft`).then(({ data }) => {
      setHasDraft(!!data.draft);
      setDraftSavedAt(data.draft?.updated_at || null);
    }).catch(() => {});
    loadDocuments();
    loadFinances();
  }, [id]);

  const loadDocuments = async () => {
    try {
      const { data } = await api.get(`/leads/${id}/documents`);
      setDocuments(data.documents || []);
    } catch { /* ignore */ }
  };

  const uploadDocument = async () => {
    if (!uploadForm.file) { toast.error("Pick a file"); return; }
    if (uploadForm.file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      if (uploadForm.label) fd.append("label", uploadForm.label);
      if (uploadForm.is_signed_contract) fd.append("is_signed_contract", "true");
      await api.post(`/leads/${id}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(uploadForm.is_signed_contract ? "Uploaded — lead marked Contract Signed" : "Uploaded");
      setShowUpload(false);
      setUploadForm({ file: null, label: "", is_signed_contract: false });
      await loadDocuments();
      // Refresh lead so the status pill updates if signed-contract was checked
      const { data } = await api.get(`/leads/${id}`);
      setLead(data.lead);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const sendDocumentForSignature = async (doc) => {
    if (!window.confirm(`Email this PDF to ${lead?.email || "the client"} for digital signature?\n\nLink expires in 7 days.`)) return;
    try {
      const { data } = await api.post(`/leads/${id}/documents/${doc.id}/send-for-signature`);
      toast.success(`Sent — link expires ${new Date(data.expires_at).toLocaleDateString()}`);
      await loadDocuments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const resp = await api.get(`/leads/${id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}?`)) return;
    try {
      await api.delete(`/leads/${id}/documents/${doc.id}`);
      toast.success("Deleted");
      await loadDocuments();
    } catch {
      toast.error("Delete failed");
    }
  };

  const loadFinances = async () => {
    try {
      const { data } = await api.get(`/leads/${id}/finances`);
      setFinances(data.finances);
      setPayments(data.payments || []);
    } catch { /* ignore */ }
  };

  // ─── Addendum helpers ────────────────────────────────────────
  const setLine = (i, patch) => {
    const lines = [...addendumForm.line_items];
    lines[i] = { ...lines[i], ...patch };
    setAddendumForm({ ...addendumForm, line_items: lines });
  };
  const addLine = () => setAddendumForm({ ...addendumForm, line_items: [...addendumForm.line_items, { description: "", qty: 1, unit_price: 0 }] });
  const removeLine = (i) => {
    const lines = [...addendumForm.line_items];
    lines.splice(i, 1);
    setAddendumForm({ ...addendumForm, line_items: lines.length ? lines : [{ description: "", qty: 1, unit_price: 0 }] });
  };
  const addendumTotal = addendumForm.line_items.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);

  const [addendumPreview, setAddendumPreview] = useState("");
  const [showAddendumPreview, setShowAddendumPreview] = useState(false);

  const previewAddendum = () => {
    const items = addendumForm.line_items.filter((l) => l.description.trim() && parseFloat(l.unit_price) > 0);
    if (items.length === 0) { toast.error("Add at least one valid line item to preview"); return; }
    const total = items.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);
    const lines = [
      `${addendumForm.title || "Contract Addendum"}`,
      "",
      `For: ${lead?.first_name || ""} ${lead?.last_name || ""}`.trim(),
      "",
      "ITEMS",
      "─".repeat(60),
      ...items.map((i, idx) => {
        const lineTotal = (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0);
        return `${(idx + 1).toString().padStart(2)}. ${i.description}    qty ${i.qty} × $${parseFloat(i.unit_price).toFixed(2)} = $${lineTotal.toFixed(2)}`;
      }),
      "─".repeat(60),
      `TOTAL: $${total.toFixed(2)}`,
      ...(addendumForm.notes ? ["", "NOTES", addendumForm.notes] : []),
    ].join("\n");
    setAddendumPreview(lines);
    setShowAddendumPreview(true);
  };

  const sendAddendum = async () => {
    const items = addendumForm.line_items.filter((l) => l.description.trim() && parseFloat(l.unit_price) > 0);
    if (items.length === 0 && !addendumForm.new_event_date) {
      toast.error("Add at least one valid line item, or pick a new event date");
      return;
    }
    try {
      await api.post(`/leads/${id}/send-addendum`, {
        title: addendumForm.title || "Contract Addendum",
        notes: addendumForm.notes,
        new_event_date: addendumForm.new_event_date || "",
        line_items: items.map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
      });
      toast.success("Addendum sent for signature");
      setShowAddendum(false);
      setAddendumForm({ title: "Contract Addendum", notes: "", new_event_date: "", line_items: [{ description: "", qty: 1, unit_price: 0 }] });
      // Refresh lead so the date change reflects immediately.
      api.get(`/leads/${id}`).then(({ data }) => setLead(data.lead)).catch(() => {});
      api.get(`/leads/${id}/contracts`).then(({ data }) => setContracts(data.contracts));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  // ─── Payments ────────────────────────────────────────────────
  const recordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/leads/${id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        method: paymentForm.method,
        received_at: paymentForm.received_at,
        note: paymentForm.note,
      });
      toast.success("Payment recorded");
      setShowPayment(false);
      setPaymentForm({ amount: "", method: "check", received_at: todayET(), note: "" });
      loadFinances();
      api.get(`/leads/${id}`).then(({ data }) => setLead(data.lead));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deletePayment = async (paymentId) => {
    const code = window.prompt("Enter passcode to delete this payment:");
    if (!code) return;
    try {
      await api.delete(`/leads/${id}/payments/${paymentId}`, { data: { passcode: code } });
      toast.success("Payment removed");
      loadFinances();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // ─── Square Payment Link ─────────────────────────────────────
  const sendSquareLink = async () => {
    const balance = Math.max(0, finances.balance || 0);
    const defaultAmt = balance > 0 ? balance.toFixed(2) : "";
    const raw = window.prompt(
      `Send Square payment link to ${lead.email}?\n\nEnter amount in USD (e.g. 250.00).`,
      defaultAmt
    );
    if (raw === null) return;
    const amount = parseFloat(String(raw).replace(/[$,]/g, ""));
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post(`/leads/${id}/payments/square-link`, {
        amount,
        send_email: true,
      });
      toast.success(`Payment link emailed (Square ${data.link.environment}) — $${amount.toFixed(2)}`);
      loadSquareLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Square link failed");
    }
  };

  const [squareLinks, setSquareLinks] = useState([]);
  const loadSquareLinks = async () => {
    try {
      const { data } = await api.get(`/leads/${id}/payments/square-links`);
      setSquareLinks(data.links || []);
    } catch { /* swallow */ }
  };
  useEffect(() => { if (id) loadSquareLinks(); /* eslint-disable-next-line */ }, [id]);

  const deleteSquareLink = async (linkId) => {
    if (!window.confirm("Remove this payment link from the list? (The Square link itself stays alive — this just hides it here.)")) return;
    try {
      await api.delete(`/leads/${id}/payments/square-link/${linkId}`);
      toast.success("Link removed");
      loadSquareLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // ─── Square Invoices ──────────────────────────────────────────
  const [squareInvoices, setSquareInvoices] = useState([]);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invForm, setInvForm] = useState({
    title: "", description: "",
    line_items: [{ description: "", qty: 1, unit_price: "" }],
    deposit_pct: 50, balance_due_date: "", allow_ach: true, auto_reminders: true,
  });
  const [invSending, setInvSending] = useState(false);

  const loadSquareInvoices = async () => {
    try {
      const { data } = await api.get(`/leads/${id}/payments/square-invoices`);
      setSquareInvoices(data.invoices || []);
    } catch { /* swallow */ }
  };
  useEffect(() => { if (id) loadSquareInvoices(); /* eslint-disable-next-line */ }, [id]);

  const openInvoiceForm = () => {
    // Prefill: one line item using the lead's stuff, balance due 30 days out.
    const balanceDue = new Date();
    balanceDue.setDate(balanceDue.getDate() + 30);
    const due = balanceDue.toISOString().slice(0, 10);
    const total = parseFloat(lead?.total_amount || finances.total || "0") || "";
    const facility = lead?.location === "rrdc_field" ? "RRDC Field" : "Turf House";
    setInvForm({
      title: `${facility} — ${lead?.first_name || ""} ${lead?.last_name || ""}`.trim(),
      description: "",
      line_items: [{
        description: lead?.requested_date ? `Booking on ${lead.requested_date}` : "Facility rental",
        qty: 1,
        unit_price: total || "",
      }],
      deposit_pct: 50,
      balance_due_date: due,
      allow_ach: true,
      auto_reminders: true,
    });
    setShowInvoice(true);
  };

  const updateLineItem = (idx, key, val) => {
    const items = invForm.line_items.map((li, i) => i === idx ? { ...li, [key]: val } : li);
    setInvForm({ ...invForm, line_items: items });
  };
  const addLineItem = () => setInvForm({ ...invForm, line_items: [...invForm.line_items, { description: "", qty: 1, unit_price: "" }] });
  const removeLineItem = (idx) => setInvForm({ ...invForm, line_items: invForm.line_items.filter((_, i) => i !== idx) });

  const invoiceTotalCents = invForm.line_items.reduce((sum, li) => {
    const p = parseFloat(li.unit_price) || 0;
    const q = parseFloat(li.qty) || 0;
    return sum + Math.round(p * q * 100);
  }, 0);

  const sendInvoice = async () => {
    if (!lead?.email) { toast.error("Lead has no email — invoices need a client email"); return; }
    if (invForm.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    if (invForm.line_items.some(li => !li.description || !(parseFloat(li.unit_price) >= 0))) { toast.error("Fill description and price for every line item"); return; }
    if (invoiceTotalCents <= 0) { toast.error("Total must be greater than $0"); return; }
    setInvSending(true);
    try {
      const payload = {
        title: invForm.title || undefined,
        description: invForm.description || undefined,
        line_items: invForm.line_items.map(li => ({
          description: li.description,
          qty: parseFloat(li.qty) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
        })),
        deposit_pct: parseFloat(invForm.deposit_pct) || 0,
        balance_due_date: invForm.balance_due_date || undefined,
        allow_ach: !!invForm.allow_ach,
        auto_reminders: !!invForm.auto_reminders,
      };
      const { data } = await api.post(`/leads/${id}/payments/square-invoice`, payload);
      const inv = data.invoice;
      toast.success(`Invoice ${inv.square_invoice_number} sent — $${(inv.total_cents/100).toFixed(2)}`);
      setShowInvoice(false);
      loadSquareInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invoice failed");
    } finally {
      setInvSending(false);
    }
  };

  const cancelInvoice = async (squareInvoiceId, num) => {
    if (!window.confirm(`Cancel invoice ${num}? Client will no longer be able to pay this link.`)) return;
    try {
      await api.post(`/leads/${id}/payments/square-invoice/${encodeURIComponent(squareInvoiceId)}/cancel`);
      toast.success("Invoice cancelled");
      loadSquareInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
  };

  const cancelContract = async (contractId, isAddendum) => {
    const code = window.prompt(`Enter passcode to cancel this ${isAddendum ? "addendum" : "contract"}:`);
    if (!code) return;
    try {
      await api.post(`/contracts/${contractId}/cancel`, { passcode: code });
      toast.success("Cancelled");
      api.get(`/leads/${id}/contracts`).then(({ data }) => setContracts(data.contracts));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const downloadCombined = async () => {
    try {
      const res = await api.get(`/leads/${id}/combined-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${lead?.first_name}_${lead?.last_name}_combined.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No signed contracts found");
    }
  };

  const createNewBooking = async () => {
    if (!newBookingForm.inquiry_type) { toast.error("Pick an inquiry type"); return; }
    try {
      const { data } = await api.post(`/leads/from-client/${id}`, newBookingForm);
      toast.success("New booking created");
      setShowNewBooking(false);
      setNewBookingForm({ inquiry_type: "rental", location: "rrdc_field", requested_date: "", start_time: "", duration: "" });
      navigate(`/leads/${data.lead.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  const updateLead = async (updates) => {
    try {
      const { data } = await api.put(`/leads/${id}`, updates);
      setLead(data.lead);
      toast.success("Lead updated");
    } catch { toast.error("Update failed"); }
  };

  const openEditLead = () => {
    setEditLeadForm({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      organization: lead.organization || "",
      street: lead.street || "",
      city: lead.city || "",
      state: lead.state || "",
      zip_code: lead.zip_code || "",
    });
    setShowEditLead(true);
  };

  const saveLeadEdit = async () => {
    if (!editLeadForm.first_name.trim() && !editLeadForm.last_name.trim() && !editLeadForm.organization.trim()) {
      toast.error("Provide at least a name or organization");
      return;
    }
    setSavingLeadEdit(true);
    try {
      const { data } = await api.put(`/leads/${id}`, editLeadForm);
      setLead(data.lead);
      setShowEditLead(false);
      toast.success("Lead details updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingLeadEdit(false);
    }
  };

  const openWaiver = () => {
    // Pick the right waiver template by default. RRDC birthday packages
    // that include the word "pool" (e.g. "Pool Party Package") should
    // default to the swim waiver — everything else defaults to the
    // standard Release & Waiver of Liability.
    const waivers = (templates || []).filter((t) => t.active && t.type === "waiver");
    const pkg = (lead?.package_tier || lead?.venue_option || "").toLowerCase();
    const pickPool = pkg.includes("pool");
    const poolWaiver = waivers.find((t) => (t.name || "").toLowerCase().includes("pool"));
    const standardWaiver = waivers.find((t) => !((t.name || "").toLowerCase().includes("pool")));
    const defaultWaiver = (pickPool && poolWaiver) ? poolWaiver : (standardWaiver || waivers[0]);
    // The waiver is always signed by the organization on behalf of all
    // participants — no individual player name is collected.
    const leadAddress = [lead?.street, lead?.city, lead?.state, lead?.zip_code]
      .map((s) => (s || "").trim()).filter(Boolean).join(", ").replace(/, ([A-Z]{2}), /, ", $1 ");
    setWaiverForm({
      waiver_id: defaultWaiver?.id || "",
      renter_first_name: "",
      renter_last_name: "",
      renter_email: lead?.email || "",
      renter_phone: lead?.phone || "",
      renter_organization: lead?.organization || "",
      renter_address: leadAddress,
      update_lead: false,
    });
    setShowWaiver(true);
  };

  const sendWaiver = async () => {
    if (!waiverForm.waiver_id) { toast.error("Select a waiver"); return; }
    if (!waiverForm.renter_organization.trim()) { toast.error("Organization is required"); return; }
    if (!waiverForm.renter_email.trim()) { toast.error("Recipient email is required"); return; }
    setSendingWaiver(true);
    try {
      const { data } = await api.post(`/leads/${id}/send-waiver`, waiverForm);
      if (data.lead) setLead(data.lead);
      try {
        const { data: cd } = await api.get(`/leads/${id}/contracts`);
        setContracts(cd.contracts || []);
      } catch { /* non-fatal */ }
      setShowWaiver(false);
      toast.success(`Waiver sent to ${waiverForm.renter_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send waiver");
    } finally {
      setSendingWaiver(false);
    }
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    try {
      const { data } = await api.post(`/leads/${id}/notes`, { content: noteContent, tag: noteTag });
      setNotes([data.note, ...notes]);
      setNoteContent("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const handlePasscodeAction = async () => {
    if (passcode !== "1234") { toast.error("Invalid passcode"); return; }
    try {
      if (passcodeDialog === "delete") {
        await api.delete(`/leads/${id}`, { data: { passcode: "1234" } });
        toast.success("Lead deleted");
        navigate("/leads");
      } else if (passcodeDialog === "release") {
        await api.post(`/leads/${id}/release`, { passcode: "1234" });
        toast.success("Reservation released");
        const { data } = await api.get(`/leads/${id}`);
        setLead(data.lead);
      } else if (passcodeDialog === "cancel") {
        await api.post(`/leads/${id}/cancel`, { passcode: "1234" });
        toast.success("Booking cancelled");
        const { data } = await api.get(`/leads/${id}`);
        setLead(data.lead);
      } else if (passcodeDialog === "refund") {
        await api.post(`/leads/${id}/refund`, { passcode: "1234" });
        toast.success("Refund initiated");
        const { data } = await api.get(`/leads/${id}`);
        setLead(data.lead);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    setPasscodeDialog(null);
    setPasscode("");
  };

  const markPaid = async () => {
    try {
      await api.post(`/leads/${id}/mark-paid`);
      const { data } = await api.get(`/leads/${id}`);
      setLead(data.lead);
      toast.success("Marked as paid");
    } catch { toast.error("Failed"); }
  };

  const setFieldVal = (key, v) => setContractForm((cf) => ({
    ...cf,
    field_values: { ...(cf.field_values || {}), [key]: v },
  }));

  const previewContract = async () => {
    if (!contractForm.template_id) { toast.error("Select a template"); return; }
    try {
      const recPayload = buildRecPayload();
      const payload = recPayload ? { ...contractForm, recurrence: recPayload } : contractForm;
      const { data } = await api.post(`/leads/${id}/preview-contract`, payload);
      setContractPreview(data.preview);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Preview failed";
      toast.error(`Preview failed: ${msg}`);
      // eslint-disable-next-line no-console
      console.error("[previewContract]", err);
    }
  };

  const saveContractDraft = async () => {
    try {
      const payload = {
        ...contractForm,
        schedule_mode: scheduleMode,
        rec_form: recForm,
        custom_dates: customDates,
        recurrence_on: recurrenceOn,
      };
      const { data } = await api.post(`/leads/${id}/contract-draft`, payload);
      setHasDraft(true);
      setDraftSavedAt(data.updated_at);
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const resumeContractDraft = async () => {
    try {
      const { data } = await api.get(`/leads/${id}/contract-draft`);
      const d = data.draft;
      if (!d) { toast.error("No saved draft"); return; }
      const f = d.form || {};
      setContractForm({
        template_id: f.template_id || "",
        waiver_id: f.waiver_id || "",
        deposit_amount: f.deposit_amount || "",
        total_amount: f.total_amount || "",
        payment_schedule: f.payment_schedule || "",
        custom_terms: f.custom_terms || "",
        field_values: f.field_values || {},
        recurrence: null,
      });
      setScheduleMode(f.schedule_mode || d.schedule_mode || "single");
      setRecurrenceOn(!!f.recurrence_on || (f.schedule_mode || d.schedule_mode) === "recurring");
      if (f.rec_form) setRecForm(f.rec_form);
      if (Array.isArray(f.custom_dates)) setCustomDates(f.custom_dates);
      setShowContract(true);
      toast.success("Draft loaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load draft");
    }
  };

  const discardContractDraft = async () => {
    if (!window.confirm("Discard the saved draft for this lead?")) return;
    try {
      await api.delete(`/leads/${id}/contract-draft`);
      setHasDraft(false);
      setDraftSavedAt(null);
      toast.success("Draft discarded");
    } catch {
      toast.error("Could not discard draft");
    }
  };

  const duplicateFromContract = async (contractId) => {
    try {
      const { data } = await api.get(`/leads/${id}/contracts/${contractId}/duplicate-payload`);
      setContractForm({
        template_id: data.template_id || "",
        waiver_id: data.waiver_id || "",
        deposit_amount: data.deposit_amount || "",
        total_amount: data.total_amount || "",
        payment_schedule: data.payment_schedule || "",
        custom_terms: data.custom_terms || "",
        field_values: data.field_values || {},
        recurrence: null,
      });
      const rec = data.recurrence || {};
      const mode = (rec.mode || "weekly").toLowerCase();
      if (mode === "custom") {
        setScheduleMode("custom");
        setRecurrenceOn(false);
        setCustomDates((rec.custom_dates || []).map((d) => ({ ...d })));
      } else if ((rec.weekdays || []).length > 1 || (rec.weeks || 1) > 1) {
        setScheduleMode("recurring");
        setRecurrenceOn(true);
        setRecForm({
          weekdays: rec.weekdays || [],
          start_date: rec.start_date || "",
          weeks: rec.weeks || 1,
          start_time: rec.start_time || "",
          end_time: rec.end_time || "",
          skip_holidays: !!rec.skip_holidays,
        });
        setCustomDates([]);
      } else {
        setScheduleMode("single");
        setRecurrenceOn(false);
        setRecForm({
          weekdays: rec.weekdays || [],
          start_date: rec.start_date || "",
          weeks: 1,
          start_time: rec.start_time || "",
          end_time: rec.end_time || "",
          skip_holidays: false,
        });
        setCustomDates([]);
      }
      setShowDuplicatePicker(false);
      setShowContract(true);
      toast.success("Loaded — edit anything that's different and click Send");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load contract");
    }
  };

  const openDuplicate = async () => {
    const real = (contracts || []).filter((c) => !c.is_waiver);
    if (real.length === 0) { toast.error("No previous contracts to duplicate"); return; }
    if (real.length === 1) { await duplicateFromContract(real[0].id); return; }
    setShowDuplicatePicker(true);
  };


  const buildRecPayload = () => {
    if (scheduleMode === "custom") {
      return {
        mode: "custom",
        custom_dates: customDates
          .filter((d) => d.date && d.start_time && d.end_time)
          .map((d) => ({ date: d.date, start_time: d.start_time, end_time: d.end_time })),
      };
    }
    if (scheduleMode === "recurring") {
      return { mode: "weekly", ...recForm };
    }
    // single — auto-derive weekday from start_date so backend treats it as 1-week recurrence
    if (!recForm.start_date) return null;
    const dow = new Date(recForm.start_date + "T00:00:00").getDay();
    return {
      mode: "weekly",
      ...recForm,
      weekdays: [dow === 0 ? 6 : dow - 1],
      weeks: 1,
      skip_holidays: false,
    };
  };

  const sendContract = async (saveOnly = false) => {
    if (!contractForm.template_id) { toast.error("Select a contract template"); return; }
    if (scheduleMode === "custom") {
      const valid = customDates.filter((d) => d.date && d.start_time && d.end_time);
      if (!valid.length) { toast.error("Add at least one date with start & end time"); return; }
    } else {
      if (!recForm.start_date) { toast.error("Pick a date"); return; }
      if (!recForm.start_time || !recForm.end_time) { toast.error("Pick a start and end time"); return; }
      if (scheduleMode === "recurring" && !recForm.weekdays?.length) { toast.error("Pick at least one weekday for the recurring schedule"); return; }
      if (scheduleMode === "recurring" && (!recForm.weeks || recForm.weeks < 1)) { toast.error("Weeks must be 1 or more"); return; }
    }
    try {
      const recPayload = buildRecPayload();
      // Waiver is sent independently via the "Send / Resend Waiver" button —
      // never bundled with the contract email anymore.
      const payload = { ...contractForm, waiver_id: "n/a", recurrence: recPayload, save_only: saveOnly };
      await api.post(`/leads/${id}/send-contract`, payload);
      const sessions = recPreview?.count || 0;
      if (saveOnly) {
        toast.success(sessions > 1 ? `Contract saved — ${sessions} dates blocked (no email sent)` : "Contract saved — dates blocked (no email sent)");
      } else {
        toast.success(sessions > 1 ? `Contract sent — ${sessions} sessions scheduled` : "Contract sent!");
      }
      setShowContract(false);
      setContractForm({ template_id: "", waiver_id: "n/a", deposit_amount: "", total_amount: "", payment_schedule: "", custom_terms: "", field_values: {}, recurrence: null });
      setScheduleMode("single");
      setRecurrenceOn(false);
      setRecForm({ weekdays: [], start_date: "", weeks: 4, start_time: "18:00", end_time: "20:00", skip_holidays: true });
      setCustomDates([]);
      setRecPreview(null);
      setContractPreview("");
      setHasDraft(false);
      setDraftSavedAt(null);
      const { data } = await api.get(`/leads/${id}`);
      setLead(data.lead);
      api.get(`/leads/${id}/contracts`).then(({ data: cd }) => setContracts(cd.contracts));
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  // Debounced recurrence preview — fires for single, recurring, and custom modes.
  useEffect(() => {
    const payload = buildRecPayload();
    if (!payload) { setRecPreview(null); return; }
    if (scheduleMode === "custom" && (!payload.custom_dates || payload.custom_dates.length === 0)) {
      setRecPreview(null); return;
    }
    if (scheduleMode !== "custom" && (!payload.start_date || !payload.start_time || !payload.end_time)) {
      setRecPreview(null); return;
    }
    if (scheduleMode === "recurring" && !payload.weekdays?.length) { setRecPreview(null); return; }
    const timer = setTimeout(async () => {
      setRecLoading(true);
      try {
        const { data } = await api.post("/recurrence/preview", payload);
        setRecPreview(data);
      } catch (err) {
        setRecPreview({ error: err.response?.data?.detail || "Invalid date/time" });
      } finally {
        setRecLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleMode, JSON.stringify(recForm), JSON.stringify(customDates)]);

  // ── Auto-calc payment option amounts whenever total_amount, deposit %,
  //    or option toggles change.
  // - Option 1: deposit = total × (deposit_pct/100), balance = remainder
  //   (deposit_pct defaults to 50 if blank — matches Turf House contract)
  // - Option 2: total × 0.95 (5% pay-in-full discount)
  // - Default overtime_rate = 150 when overtime_charge is checked and field
  //   is blank (admin can override per contract)
  // Recomputes on every change so admin always sees the correct math.
  useEffect(() => {
    const fv = contractForm.field_values || {};
    const total = parseFloat(String(contractForm.total_amount || "").replace(/[^0-9.]/g, "")) || 0;
    const round2 = (n) => Math.round(n * 100) / 100;
    const updates = {};
    const isChecked = (k) => !!fv[k] && fv[k] !== "false";
    if (isChecked("payment_option_1") && total > 0) {
      // Default deposit_pct to 50 the first time the option is checked,
      // so admin can't accidentally type the deposit amount into the % field.
      const fvPct = String(fv.deposit_pct || "");
      const pctRaw = parseFloat(fvPct.replace(/[^0-9.]/g, ""));
      const pct = pctRaw > 0 && pctRaw <= 100 ? pctRaw : 50;
      if (!fvPct) updates.deposit_pct = "50";
      updates.option_1_deposit = String(round2(total * (pct / 100)));
      updates.option_1_balance = String(round2(total * (1 - pct / 100)));
    }
    if (isChecked("payment_option_2") && total > 0) {
      updates.option_2_full = String(round2(total * 0.95));
    }
    if (isChecked("overtime_charge") && (!fv.overtime_rate || fv.overtime_rate === "")) {
      updates.overtime_rate = "150";
    }
    if (Object.keys(updates).length === 0) return;
    const same = Object.keys(updates).every((k) => fv[k] === updates[k]);
    if (same) return;
    const merged = { ...fv, ...updates };
    setContractForm((cf) => ({ ...cf, field_values: merged }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractForm.total_amount,
      contractForm.field_values?.payment_option_1,
      contractForm.field_values?.payment_option_2,
      contractForm.field_values?.overtime_charge,
      contractForm.field_values?.deposit_pct]);

  // ─── Birthday auto-math (front-end preview) ───────────────────────
  // Mirrors the server-side calc in merge_contract for the RRDC Birthday
  // Party Contract. Subtotal = flat + addons. Gratuity = 10% (locked).
  // Grand Total = Subtotal + Gratuity − Gift Certificate. Balance = Grand
  // Total − Deposit.
  const isBirthdayTemplate =
    selectedTemplate?.body?.includes("{{flat_rate}}") &&
    selectedTemplate?.body?.includes("{{gift_certificate_amount}}");
  const birthdayCalc = (() => {
    if (!isBirthdayTemplate) return null;
    const fv = contractForm.field_values || {};
    const num = (v) => parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, "")) || 0;
    const flat = num(fv.flat_rate);
    const addons = num(fv.addons_amount);
    const gift = num(fv.gift_certificate_amount);
    const deposit = num(fv.deposit_amount);
    const subtotal = flat + addons;
    const gratuity = Math.round(subtotal * 0.10 * 100) / 100;
    const grand = Math.round((subtotal + gratuity - gift) * 100) / 100;
    const balance = Math.round((grand - deposit) * 100) / 100;
    return { subtotal, gratuity, grand, balance, deposit };
  })();
  // Auto-sync the computed Grand Total into the top-level total_amount
  // field (the one the lead's invoice uses) so admins don't have to
  // double-type it.
  useEffect(() => {
    if (!isBirthdayTemplate || !birthdayCalc) return;
    const desired = `${birthdayCalc.grand.toFixed(2)}`;
    const current = parseFloat(String(contractForm.total_amount || "").replace(/[^0-9.]/g, "")) || 0;
    if (Math.abs(current - birthdayCalc.grand) < 0.005) return;
    setContractForm((cf) => ({ ...cf, total_amount: desired, deposit_amount: birthdayCalc.deposit ? `${birthdayCalc.deposit.toFixed(2)}` : cf.deposit_amount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBirthdayTemplate, birthdayCalc?.grand, birthdayCalc?.deposit]);

  const toggleWeekday = (w) => {
    const cur = new Set(recForm.weekdays);
    if (cur.has(w)) cur.delete(w); else cur.add(w);
    setRecForm({ ...recForm, weekdays: Array.from(cur).sort() });
  };

  const sendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) { toast.error("Subject and body required"); return; }
    try {
      await api.post(`/leads/${id}/send-email`, { to: lead.email, ...emailForm });
      toast.success("Email sent!");
      setShowEmailer(false);
      setEmailForm({ subject: "", body: "", cc: "" });
      api.get(`/leads/${id}/emails`).then(({ data }) => setEmails(data.emails));
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  if (!lead) return <div className="text-center py-12 text-muted-foreground">Lead not found</div>;

  const deleteContract = async (doc) => {
    if (!window.confirm(`Delete this ${doc.is_waiver ? "waiver" : doc.is_addendum ? "addendum" : "contract"}? This cannot be undone.${doc.signed_at ? "\n\nThis document has been SIGNED — deleting will remove all records of the signature." : ""}`)) return;
    try {
      await api.delete(`/contracts/${doc.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteAllContracts = async () => {
    const code = window.prompt(
      `Enter passcode to delete ALL contracts, waivers, and addendums on this lead.\n\nThis cannot be undone.`,
    );
    if (!code) return;
    try {
      const { data } = await api.delete(`/leads/${id}/contracts`, { data: { passcode: code } });
      toast.success(`Deleted ${data.deleted} document${data.deleted === 1 ? "" : "s"}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="lead-detail-page">
      <button onClick={() => navigate("/leads")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm" data-testid="back-to-leads">
        <ArrowLeft size={16} /> Back to Leads
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold">{lead.first_name} {lead.last_name}</h1>
          <p className="text-muted-foreground text-sm capitalize">{lead.inquiry_type?.replace("_", " ")} - {lead.location === "rrdc_field" ? "RRDC Field" : "Turf House"}</p>
        </div>
        <Badge className={`text-sm px-3 py-1 ${lead.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"}`}>
          {STATUS_LABELS[lead.status]}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Contact & Details */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-outfit">Contact Info</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={openEditLead}
                data-testid="edit-lead-btn"
              >
                <Pencil size={12} className="mr-1" /> Edit
              </Button>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {lead.organization && (
                <div className="flex items-center gap-2"><User size={14} className="text-muted-foreground" /> {lead.organization}</div>
              )}
              <div className="flex items-center gap-2"><Mail size={14} className="text-muted-foreground" /> {lead.email || "N/A"}</div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> {lead.phone || "N/A"}</div>
              <div className="flex items-center gap-2"><MapPin size={14} className="text-muted-foreground" /> {lead.street} {lead.city}, {lead.state} {lead.zip_code}</div>
              {lead.requested_date && <div className="text-muted-foreground">Date: {lead.requested_date} {lead.start_time && `at ${lead.start_time}`}</div>}
              {lead.duration && <div className="text-muted-foreground">Duration: {lead.duration}h</div>}
              {lead.lead_source && <div className="text-muted-foreground">Source: {lead.lead_source}</div>}
              {lead.additional_notes && <div className="text-muted-foreground">Notes: {lead.additional_notes}</div>}
              {/* Supervisor field */}
              <div className="pt-2 border-t border-border mt-2 space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  {lead.brand === "riverview" ? "Turf / Birthday Supervisor" : "Field / Birthday Supervisor"}
                </label>
                <Input
                  data-testid="supervisor-input"
                  placeholder="Enter names separated by commas"
                  value={lead.supervisor || ""}
                  onChange={(e) => updateLead({ supervisor: e.target.value })}
                  className="text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Booking Information Button */}
          {(lead.status === "contract_signed" || lead.status === "paid" || contracts.length > 0) && (
            <Button
              data-testid="booking-info-btn"
              variant="outline"
              className="w-full justify-center gap-2 py-5 text-primary border-primary/30 hover:bg-primary/5"
              onClick={() => setShowBookingInfo(true)}
            >
              <ClipboardCheck size={18} /> Booking Information
            </Button>
          )}

          {/* Notes */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit">Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Input
                  data-testid="note-input"
                  placeholder="Add a note..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="flex-1 min-w-[200px]"
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                />
                <Select value={noteTag} onValueChange={setNoteTag}>
                  <SelectTrigger data-testid="note-tag-select" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_TAGS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="add-note-btn" size="sm" onClick={addNote}>Add</Button>
              </div>
              {notes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No notes yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {notes.map((n) => (
                    <div key={n.id} className="p-3 rounded-lg bg-muted/50 border border-border text-sm" data-testid={`note-${n.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${TAG_COLORS[n.tag] || "bg-gray-500/20 text-gray-400"}`}>{n.tag}</Badge>
                        <span className="text-xs text-muted-foreground">{n.author_name} - {formatET(n.created_at)}</span>
                      </div>
                      <p>{n.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={lead.status} onValueChange={(v) => updateLead({ status: v })}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Assigned To</label>
                <Select value={lead.assigned_to || "unassigned"} onValueChange={(v) => updateLead({ assigned_to: v === "unassigned" ? null : v })}>
                  <SelectTrigger data-testid="assign-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {brandUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Tour Date</label>
                <Input
                  data-testid="tour-date-input"
                  type="date"
                  value={lead.tour_date || ""}
                  onChange={(e) => updateLead({ tour_date: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="finances-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit flex items-center justify-between">
                <span>Finances</span>
                {finances.is_paid_in_full && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">Paid in Full</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Balance Due</p>
                <p className={`text-3xl font-mono font-bold ${finances.balance > 0 ? "text-amber-500" : finances.is_paid_in_full ? "text-emerald-500" : "text-foreground"}`} data-testid="finances-balance">
                  ${finances.balance.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Total ${finances.total_due.toFixed(2)} · Paid ${finances.paid.toFixed(2)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowPayment(true)} data-testid="record-payment-btn">
                  <Receipt size={14} /> Record Payment
                </Button>
                {finances.contract_count > 0 && (
                  <Button size="sm" variant="outline" onClick={downloadCombined} data-testid="combined-pdf-btn">
                    <Download size={14} />
                  </Button>
                )}
              </div>
              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendSquareLink} data-testid="send-square-link-btn" disabled={!lead?.email}>
                <CreditCard size={14} /> Send Square Payment Link
              </Button>
              <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={openInvoiceForm} data-testid="send-square-invoice-btn" disabled={!lead?.email}>
                <FileText size={14} /> Send Square Invoice (cards + ACH)
              </Button>
              {!lead?.email && (
                <p className="text-[10px] text-amber-500">Add an email on the lead to send a payment link or invoice.</p>
              )}
              {squareLinks.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-border max-h-40 overflow-y-auto" data-testid="square-links-list">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Square Links</p>
                  {squareLinks.map((sl) => (
                    <div key={sl.id} className="flex items-center justify-between gap-2 text-xs py-1">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-medium flex items-center gap-1.5">
                          ${(sl.amount_cents / 100).toFixed(2)}
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${sl.status === "paid" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" : "bg-amber-500/10 text-amber-500 border-amber-500/40"}`}>
                            {sl.status}
                          </Badge>
                          {sl.environment === "sandbox" && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-500 border-blue-500/40">test</Badge>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{formatET(sl.created_at)}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <a href={sl.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="Open checkout link">
                          <ExternalLink size={12} />
                        </a>
                        <button onClick={() => deleteSquareLink(sl.id)} className="text-muted-foreground hover:text-rose-500" title="Remove from list" data-testid={`square-link-delete-${sl.id}`}>
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {squareInvoices.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-border max-h-48 overflow-y-auto" data-testid="square-invoices-list">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Square Invoices</p>
                  {squareInvoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-2 text-xs py-1 group">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-medium flex items-center gap-1.5 flex-wrap">
                          #{inv.square_invoice_number} ${(inv.total_cents / 100).toFixed(2)}
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${inv.status === "PAID" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" : inv.status === "PARTIALLY_PAID" ? "bg-blue-500/15 text-blue-500 border-blue-500/40" : inv.status === "CANCELED" ? "bg-rose-500/15 text-rose-500 border-rose-500/40" : "bg-amber-500/10 text-amber-500 border-amber-500/40"}`}>
                            {inv.status}
                          </Badge>
                          {inv.allow_ach && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-indigo-500/10 text-indigo-500 border-indigo-500/40">ACH</Badge>}
                          {inv.environment === "sandbox" && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-500 border-blue-500/40">test</Badge>}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">{inv.title}{inv.deposit_cents ? ` · deposit $${(inv.deposit_cents/100).toFixed(2)}` : ""}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <a href={inv.public_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="Open invoice">
                          <ExternalLink size={12} />
                        </a>
                        {(inv.status !== "PAID" && inv.status !== "CANCELED") && (
                          <button onClick={() => cancelInvoice(inv.square_invoice_id, inv.square_invoice_number)} className="text-muted-foreground hover:text-rose-500" title="Cancel invoice">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {payments.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-border max-h-48 overflow-y-auto">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Payments</p>
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 text-xs py-1.5 group">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-medium">${p.amount.toFixed(2)} <span className="text-muted-foreground font-sans capitalize">· {p.method}</span></p>
                        <p className="text-[10px] text-muted-foreground">{formatDateET(p.received_at)} · saved {formatET(p.recorded_at)} by {p.recorded_by_username}{p.note ? ` · "${p.note}"` : ""}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => deletePayment(p.id)}>
                        <Trash2 size={11} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button data-testid="send-contract-btn" variant="outline" size="sm" className="w-full justify-start text-primary" onClick={() => {
                // Prefill date/times from the lead's existing inquiry data
                const dur = parseFloat(lead?.duration || "2") || 2;
                const start = lead?.start_time || "18:00";
                let endH = parseInt(start.split(":")[0], 10) + Math.floor(dur);
                let endM = parseInt(start.split(":")[1] || "0", 10) + Math.round((dur - Math.floor(dur)) * 60);
                if (endM >= 60) { endH += 1; endM -= 60; }
                const end = `${String(endH).padStart(2,"0")}:${String(endM).padStart(2,"0")}`;
                setRecForm((prev) => ({
                  ...prev,
                  start_date: lead?.requested_date || prev.start_date,
                  start_time: start,
                  end_time: end,
                }));
                setShowContract(true);
              }}>
                <FileText size={14} /> Send Contract
              </Button>
              {(contracts || []).filter((c) => !c.is_waiver).length > 0 && (
                <Button data-testid="duplicate-contract-btn" variant="outline" size="sm" className="w-full justify-start" onClick={openDuplicate}>
                  <FileText size={14} /> Duplicate to New Contract
                </Button>
              )}
              {hasDraft && (
                <div className="flex items-stretch gap-1.5">
                  <Button data-testid="resume-draft-btn" variant="outline" size="sm" className="flex-1 justify-start text-emerald-600 hover:text-emerald-500 border-emerald-500/40" onClick={resumeContractDraft} title={draftSavedAt ? `Saved ${formatET(draftSavedAt)}` : ""}>
                    <FileText size={14} /> Continue working on contract
                  </Button>
                  <Button data-testid="discard-draft-btn" variant="outline" size="sm" className="px-2 text-rose-500 hover:text-rose-400 border-rose-500/30" onClick={discardContractDraft} title="Discard draft">
                    <XCircle size={14} />
                  </Button>
                </div>
              )}
              <Button data-testid="send-addendum-btn" variant="outline" size="sm" className="w-full justify-start" onClick={() => setShowAddendum(true)}>
                <FilePlus2 size={14} /> Send Addendum
              </Button>
              <Button data-testid="send-waiver-btn" variant="outline" size="sm" className="w-full justify-start" onClick={openWaiver}>
                <FileText size={14} /> Send / Resend Waiver
              </Button>
              <Button data-testid="send-email-btn" variant="outline" size="sm" className="w-full justify-start" onClick={() => setShowEmailer(true)}>
                <Mail size={14} /> Send Email
              </Button>
              <Button data-testid="upload-doc-btn" variant="outline" size="sm" className="w-full justify-start" onClick={() => setShowUpload(true)}>
                <Upload size={14} /> Upload Document
              </Button>
              <Button data-testid="mark-paid-btn" variant="outline" size="sm" className="w-full justify-start" onClick={markPaid}>
                <DollarSign size={14} /> Mark Paid
              </Button>
              <Button data-testid="cancel-btn" variant="outline" size="sm" className="w-full justify-start text-amber-500 hover:text-amber-400" onClick={() => setPasscodeDialog("cancel")}>
                <XCircle size={14} /> Cancel Booking
              </Button>
              <Button data-testid="refund-btn" variant="outline" size="sm" className="w-full justify-start text-orange-500 hover:text-orange-400" onClick={() => setPasscodeDialog("refund")}>
                <RotateCcw size={14} /> Refund
              </Button>
              <Button data-testid="release-btn" variant="outline" size="sm" className="w-full justify-start" onClick={() => setPasscodeDialog("release")}>
                <Unlock size={14} /> Release Reservation
              </Button>
              <Button data-testid="delete-lead-btn" variant="destructive" size="sm" className="w-full justify-start" onClick={() => setPasscodeDialog("delete")}>
                <Trash2 size={14} /> Delete Lead
              </Button>
              <Button data-testid="delete-all-contracts-btn" variant="outline" size="sm" className="w-full justify-start text-rose-500 hover:text-rose-400" onClick={deleteAllContracts}>
                <Trash2 size={14} /> Delete All Contracts
              </Button>
            </CardContent>
          </Card>
          {/* Bookings — current + past with all files & audit per booking */}
          <Card className="border-border" data-testid="booking-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit flex items-center justify-between">
                <span>Bookings</span>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setShowNewBooking(true)} data-testid="new-booking-btn">
                  <Plus size={12} /> New Booking
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {related.current.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold">Current</p>
                  {related.current.map((rl) => (
                    <BookingHistoryRow key={rl.id} bk={rl} active={rl.id === id} onOpen={() => navigate(`/leads/${rl.id}`)} />
                  ))}
                </div>
              )}
              {related.past.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-border">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Past</p>
                  {related.past.map((rl) => (
                    <BookingHistoryRow key={rl.id} bk={rl} active={rl.id === id} onOpen={() => navigate(`/leads/${rl.id}`)} />
                  ))}
                </div>
              )}
              {related.current.length === 0 && related.past.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No bookings loaded yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Information Dialog */}
      <Dialog open={showBookingInfo} onOpenChange={setShowBookingInfo}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardCheck size={20} className="text-primary" /> Booking Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Signed Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Contract Signed</p>
                  <p className="text-sm font-medium">{lead.contract_signed_at ? formatET(lead.contract_signed_at) : "Not yet signed"}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Waiver Signed</p>
                  <p className="text-sm font-medium">{lead.waiver_signed_at ? formatET(lead.waiver_signed_at) : "Not yet signed"}</p>
                </CardContent>
              </Card>
            </div>

            {/* Supervisor */}
            <Card className="border-border">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  {lead.brand === "riverview" ? "Turf / Birthday Supervisor" : "Field / Birthday Supervisor"}
                </p>
                <p className="text-sm font-medium">{lead.supervisor || "Not assigned"}</p>
              </CardContent>
            </Card>

            {/* Booking Notes */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Booking Notes</p>
                <textarea
                  data-testid="booking-notes-input"
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[80px]"
                  placeholder="Add booking-specific notes..."
                  value={lead.booking_notes || ""}
                  onChange={(e) => updateLead({ booking_notes: e.target.value })}
                />
              </CardContent>
            </Card>

            {/* Contracts & Waivers - Downloadable */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Documents</p>
                {contracts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No contracts sent yet</p>
                ) : (
                  <div className="space-y-2">
                    {contracts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText size={16} className={c.is_waiver ? "text-amber-500" : c.is_addendum ? "text-purple-500" : "text-primary"} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{c.template_name}{c.is_addendum && c.total_amount ? ` — $${parseFloat(c.total_amount).toFixed(2)}` : ""}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className={`text-xs ${c.status === "signed" ? "text-green-500 border-green-500/30" : "text-amber-500 border-amber-500/30"}`}>
                                {c.status}
                              </Badge>
                              <span>{c.is_waiver ? "Waiver" : c.is_addendum ? "Addendum" : "Contract"}</span>
                              {c.signed_at && <span>Signed: {formatDateET(c.signed_at)}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {c.status !== "signed" && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => cancelContract(c.id, c.is_addendum)} data-testid={`cancel-doc-${c.id}`}>
                              <XCircle size={14} />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewContract(c)} data-testid={`view-doc-${c.id}`}>
                            <Eye size={14} />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            data-testid={`download-doc-${c.id}`}
                            onClick={async () => {
                              try {
                                const res = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
                                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `${(c.template_name || "contract").replace(/\s+/g, "_")}_${c.is_waiver ? "waiver" : "contract"}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                setTimeout(() => URL.revokeObjectURL(url), 1000);
                              } catch (err) {
                                const detail = err?.response?.data?.detail || err?.message || "Unknown";
                                toast.error(`Could not download PDF: ${detail}`);
                                // eslint-disable-next-line no-console
                                console.error("[download contract pdf]", err);
                              }
                            }}
                          >
                            <Download size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Uploaded Documents (external files) */}
            {documents.length > 0 && (
              <Card className="border-border">
                <CardContent className="p-4 space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Uploaded Files</p>
                  <div className="space-y-2">
                    {documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border" data-testid={`document-row-${d.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Paperclip size={16} className={d.is_signed_contract ? "text-green-500" : "text-muted-foreground"} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{d.filename}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                              {d.is_signed_contract && <Badge variant="outline" className="text-xs text-green-500 border-green-500/30">Signed Contract</Badge>}
                              {d.signature_status === "sent" && <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">Awaiting Signature</Badge>}
                              {d.signature_status === "signed" && !d.is_signed_contract && <Badge variant="outline" className="text-xs text-green-500 border-green-500/30">Client Signed</Badge>}
                              {d.label && <span>{d.label}</span>}
                              <span>{(d.size / 1024).toFixed(1)} KB</span>
                              <span>· {formatDateET(d.uploaded_at)}</span>
                              {d.uploaded_by_username && <span>· by {d.uploaded_by_username}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {d.mime === "application/pdf" && d.signature_status !== "signed" && !d.is_signed_contract && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-600" onClick={() => sendDocumentForSignature(d)} data-testid={`send-sig-document-${d.id}`} title={d.signature_status === "sent" ? "Resend for signature" : "Send for digital signature"}>
                              <Send size={14} />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadDocument(d)} data-testid={`download-document-${d.id}`}>
                            <Download size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:text-rose-600" onClick={() => deleteDocument(d)} data-testid={`delete-document-${d.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {emails.length > 0 && (
              <Card className="border-border">
                <CardContent className="p-4 space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Email History</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {emails.map((e) => (
                      <div key={e.id} className="p-2 rounded bg-muted/30 border border-border text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">{e.subject}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{formatET(e.sent_at)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">To: {e.to} | By: {e.sent_by}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Contract Picker (only when >1 prior contracts) */}
      <Dialog open={showDuplicatePicker} onOpenChange={setShowDuplicatePicker}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-6" style={{ maxWidth: "min(520px, calc(100vw - 16px))", width: "calc(100vw - 16px)" }}>
          <DialogHeader>
            <DialogTitle>Choose contract to duplicate</DialogTitle>
            <p className="text-xs text-muted-foreground">Pick which contract to copy. You can edit anything before sending the new one.</p>
          </DialogHeader>
          <div className="space-y-2">
            {(contracts || []).filter((c) => !c.is_waiver).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => duplicateFromContract(c.id)}
                data-testid={`duplicate-pick-${c.id}`}
                className="w-full text-left p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <p className="text-sm font-medium">{c.template_name || "Contract"}</p>
                <p className="text-xs text-muted-foreground">Sent {formatET(c.sent_at)} · {c.occurrence_count ? `${c.occurrence_count} session${c.occurrence_count > 1 ? "s" : ""}` : "1 session"}{c.total_amount ? ` · Total $${c.total_amount}` : ""}</p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicatePicker(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUpload} onOpenChange={(v) => { if (!v) setUploadForm({ file: null, label: "", is_signed_contract: false }); setShowUpload(v); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-6" style={{ maxWidth: "min(560px, calc(100vw - 16px))", width: "calc(100vw - 16px)" }}>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <p className="text-xs text-muted-foreground">PDF, Word, Excel, or images. Max 25 MB.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">File</label>
              <input
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                data-testid="upload-file-input"
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              />
              {uploadForm.file && (
                <p className="text-xs text-muted-foreground">{uploadForm.file.name} — {(uploadForm.file.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Label (optional)</label>
              <Input
                value={uploadForm.label}
                onChange={(e) => setUploadForm({ ...uploadForm, label: e.target.value })}
                placeholder="e.g., Signed insurance certificate"
                data-testid="upload-label-input"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={uploadForm.is_signed_contract}
                onChange={(e) => setUploadForm({ ...uploadForm, is_signed_contract: e.target.checked })}
                data-testid="upload-signed-contract-checkbox"
              />
              <span>
                <span className="font-medium">External Signed Contract</span>
                <span className="block text-xs text-muted-foreground">Tag as a signed contract — lead status will flip to "Contract Signed" and calendar blocks will be marked booked.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={uploadDocument} disabled={uploading || !uploadForm.file} data-testid="upload-submit-btn">
              <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Contract Dialog */}
      <Dialog open={showContract} onOpenChange={(v) => { if (!v) { setContractPreview(""); } setShowContract(v); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-6" style={{ maxWidth: "min(640px, calc(100vw - 16px))", width: "calc(100vw - 16px)" }}>
          <DialogHeader>
            <DialogTitle>Send Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Renter Information — override fields (blank = use lead record) */}
            <details className="rounded-lg border border-border bg-muted/20" data-testid="renter-info-section">
              <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium flex items-center justify-between">
                <span>Renter Information <span className="text-xs text-muted-foreground font-normal">(blank fields use lead data)</span></span>
                <ChevronDown size={14} className="opacity-60" />
              </summary>
              <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">First Name</label>
                  <Input
                    placeholder={lead?.first_name || "—"}
                    value={contractForm.field_values?.renter_first_name || ""}
                    onChange={(e) => setFieldVal("renter_first_name", e.target.value)}
                    data-testid="renter-first-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Last Name</label>
                  <Input
                    placeholder={lead?.last_name || "—"}
                    value={contractForm.field_values?.renter_last_name || ""}
                    onChange={(e) => setFieldVal("renter_last_name", e.target.value)}
                    data-testid="renter-last-name-input"
                  />
                </div>
                <div className="space-y-1 col-span-1 sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Organization</label>
                  <Input
                    placeholder={lead?.organization || "—"}
                    value={contractForm.field_values?.renter_organization || ""}
                    onChange={(e) => setFieldVal("renter_organization", e.target.value)}
                    data-testid="renter-organization-input"
                  />
                </div>
                <div className="space-y-1 col-span-1 sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Mailing Address</label>
                  <Input
                    placeholder={[lead?.street, lead?.city, lead?.state, lead?.zip_code].filter(Boolean).join(", ") || "—"}
                    value={contractForm.field_values?.renter_address || ""}
                    onChange={(e) => setFieldVal("renter_address", e.target.value)}
                    data-testid="renter-address-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Phone</label>
                  <Input
                    placeholder={lead?.phone || "—"}
                    value={contractForm.field_values?.renter_phone || ""}
                    onChange={(e) => setFieldVal("renter_phone", e.target.value)}
                    data-testid="renter-phone-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Email</label>
                  <Input
                    placeholder={lead?.email || "—"}
                    value={contractForm.field_values?.renter_email || ""}
                    onChange={(e) => setFieldVal("renter_email", e.target.value)}
                    data-testid="renter-email-input"
                  />
                </div>
              </div>
            </details>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Contract Template</label>
              <Select value={contractForm.template_id} onValueChange={(v) => { setContractForm({ ...contractForm, template_id: v, field_values: {} }); setContractPreview(""); }}>
                <SelectTrigger data-testid="contract-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.active).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.type?.replace("_", " ")})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Deposit Amount</label>
                <Input data-testid="contract-deposit" placeholder="$0.00" value={contractForm.deposit_amount} onChange={(e) => setContractForm({ ...contractForm, deposit_amount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Total Amount</label>
                <Input data-testid="contract-total" placeholder="$0.00" value={contractForm.total_amount} onChange={(e) => setContractForm({ ...contractForm, total_amount: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Payment Schedule</label>
              <Input data-testid="contract-schedule" placeholder="e.g. 50% deposit, balance due 7 days before event" value={contractForm.payment_schedule} onChange={(e) => setContractForm({ ...contractForm, payment_schedule: e.target.value })} />
            </div>
            {templateFields.length > 0 && (
              <div className="space-y-2 rounded-lg border border-border p-3 bg-muted/20">
                <p className="text-sm font-medium">Template Fields</p>
                <div className="grid grid-cols-2 gap-3">
                  {templateFields.map((f) => {
                    const val = contractForm.field_values?.[f.key] ?? "";
                    const setVal = (v) => setContractForm({
                      ...contractForm,
                      field_values: { ...(contractForm.field_values || {}), [f.key]: v },
                    });
                    if (f.type === "checkbox") {
                      const checked = !!val && val !== "false";
                      return (
                        <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm col-span-2 sm:col-span-1">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            onChange={(e) => setVal(e.target.checked ? "true" : "")}
                            data-testid={`tpl-field-${f.key}`}
                          />
                          <span>{f.label}</span>
                          <code className="text-[10px] text-muted-foreground ml-auto hidden md:inline-block truncate max-w-[180px]">{`{{${f.key}}}`}</code>
                        </label>
                      );
                    }
                    if (f.type === "select" && Array.isArray(f.options)) {
                      return (
                        <div key={f.key} className="space-y-1 col-span-2 sm:col-span-1 min-w-0">
                          <label className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                            <span className="truncate">{f.label}</span>
                            <code className="text-[10px] hidden md:inline-block truncate max-w-[160px]">{`{{${f.key}}}`}</code>
                          </label>
                          <select
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            data-testid={`tpl-field-${f.key}`}
                            className="w-full bg-background border border-border rounded-md h-9 px-3 text-sm"
                          >
                            <option value="">— Select —</option>
                            {f.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    return (
                      <div key={f.key} className="space-y-1 col-span-2 sm:col-span-1 min-w-0">
                        <label className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                          <span className="truncate">{f.label}</span>
                          <code className="text-[10px] hidden md:inline-block truncate max-w-[160px]">{`{{${f.key}}}`}</code>
                        </label>
                        <Input
                          type={f.type === "number" ? "number" : (f.type === "date" ? "date" : "text")}
                          value={val}
                          onChange={(e) => setVal(e.target.value)}
                          data-testid={`tpl-field-${f.key}`}
                          placeholder={f.label}
                          className="w-full min-w-0"
                        />
                      </div>
                    );
                  })}
                </div>
                {isBirthdayTemplate && birthdayCalc && (
                  <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-4 space-y-1.5 text-sm" data-testid="birthday-calc">
                    <p className="font-semibold text-amber-900 dark:text-amber-200 mb-2">Auto-calculated</p>
                    <Row label="Subtotal (Flat + Add-ons)" v={birthdayCalc.subtotal} />
                    <Row label="Gratuity (10% locked)" v={birthdayCalc.gratuity} />
                    <Row label="Grand Total" v={birthdayCalc.grand} bold />
                    <Row label="Deposit" v={birthdayCalc.deposit} />
                    <Row label="Balance Due" v={birthdayCalc.balance} bold />
                    <p className="text-[11px] text-muted-foreground pt-1">These update the lead's total + deposit automatically.</p>
                  </div>
                )}
              </div>
            )}
            {/* ── Schedule selector: Single | Recurring | Custom dates ── */}
            <div className="space-y-3 border border-border rounded-lg p-4 bg-muted/20">
              <p className="text-sm font-medium">Schedule</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: "single", l: "Single date" },
                  { v: "recurring", l: "Repeat weekly" },
                  { v: "custom", l: "Multiple dates" },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => { setScheduleMode(m.v); setRecurrenceOn(m.v === "recurring"); }}
                    data-testid={`schedule-mode-${m.v}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${scheduleMode === m.v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`}
                  >{m.l}</button>
                ))}
              </div>

              {scheduleMode !== "custom" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{scheduleMode === "recurring" ? "Start date" : "Date"}</label>
                    <Input type="date" value={recForm.start_date} onChange={(e) => setRecForm({...recForm, start_date: e.target.value})} data-testid="rec-start-date" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Start time</label>
                    <Input type="time" value={recForm.start_time} onChange={(e) => setRecForm({...recForm, start_time: e.target.value})} data-testid="rec-start-time" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">End time</label>
                    <Input type="time" value={recForm.end_time} onChange={(e) => setRecForm({...recForm, end_time: e.target.value})} data-testid="rec-end-time" />
                  </div>
                </div>
              )}

              {scheduleMode === "custom" && (
                <div className="space-y-2" data-testid="custom-dates-panel">
                  <p className="text-xs text-muted-foreground">Add each date individually with its start/end time. Hours auto-calculate.</p>
                  {customDates.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No dates added yet.</p>
                  )}
                  {customDates.map((d, idx) => {
                    const hours = (() => {
                      if (!d.start_time || !d.end_time) return "";
                      const [sh, sm] = d.start_time.split(":").map(Number);
                      const [eh, em] = d.end_time.split(":").map(Number);
                      const h = (eh * 60 + em - sh * 60 - sm) / 60;
                      return h > 0 ? `${h}h` : "";
                    })();
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`custom-date-row-${idx}`}>
                        <div className="col-span-4 space-y-1">
                          <label className="text-[10px] text-muted-foreground">Date</label>
                          <Input type="date" value={d.date || ""} onChange={(e) => {
                            const next = [...customDates]; next[idx] = { ...next[idx], date: e.target.value }; setCustomDates(next);
                          }} data-testid={`custom-date-${idx}`} />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <label className="text-[10px] text-muted-foreground">Start</label>
                          <Input type="time" value={d.start_time || ""} onChange={(e) => {
                            const next = [...customDates]; next[idx] = { ...next[idx], start_time: e.target.value }; setCustomDates(next);
                          }} data-testid={`custom-start-${idx}`} />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <label className="text-[10px] text-muted-foreground">End</label>
                          <Input type="time" value={d.end_time || ""} onChange={(e) => {
                            const next = [...customDates]; next[idx] = { ...next[idx], end_time: e.target.value }; setCustomDates(next);
                          }} data-testid={`custom-end-${idx}`} />
                        </div>
                        <div className="col-span-1 text-xs text-muted-foreground pb-2 text-center">{hours}</div>
                        <div className="col-span-1 pb-1">
                          <button type="button" onClick={() => setCustomDates(customDates.filter((_, i) => i !== idx))}
                                  data-testid={`custom-remove-${idx}`}
                                  className="text-rose-500 hover:text-rose-600 text-xs">✕</button>
                        </div>
                      </div>
                    );
                  })}
                  <Button type="button" variant="outline" size="sm" onClick={() => setCustomDates([...customDates, { date: "", start_time: "11:00", end_time: "13:00" }])} data-testid="custom-add-date-btn">
                    + Add date
                  </Button>
                  {customDates.length > 0 && (
                    <p className="text-[11px] text-muted-foreground pt-1">
                      Total hours: {customDates.reduce((acc, d) => {
                        if (!d.start_time || !d.end_time) return acc;
                        const [sh, sm] = d.start_time.split(":").map(Number);
                        const [eh, em] = d.end_time.split(":").map(Number);
                        const h = (eh * 60 + em - sh * 60 - sm) / 60;
                        return acc + (h > 0 ? h : 0);
                      }, 0)} · Total sessions: {customDates.filter(d => d.date).length}
                    </p>
                  )}
                </div>
              )}

              {scheduleMode === "recurring" && (
                <div className="space-y-3 pl-2 border-l-2 border-primary/30" data-testid="recurrence-panel">
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Days of week</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {[{v:0,l:"Mon"},{v:1,l:"Tue"},{v:2,l:"Wed"},{v:3,l:"Thu"},{v:4,l:"Fri"},{v:5,l:"Sat"},{v:6,l:"Sun"}].map((d) => (
                        <button
                          key={d.v}
                          type="button"
                          onClick={() => toggleWeekday(d.v)}
                          data-testid={`rec-weekday-${d.l.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${recForm.weekdays.includes(d.v) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`}
                        >{d.l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1 max-w-[200px]">
                    <label className="text-xs text-muted-foreground">Repeat for weeks</label>
                    <Input type="number" min="1" max="104" value={recForm.weeks} onChange={(e) => setRecForm({...recForm, weeks: parseInt(e.target.value || "0", 10)})} data-testid="rec-weeks" />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={recForm.skip_holidays}
                      onChange={(e) => setRecForm({...recForm, skip_holidays: e.target.checked})}
                      data-testid="rec-skip-holidays"
                    />
                    <span>Skip US federal holidays</span>
                  </label>
                </div>
              )}

              {recLoading && (
                <p className="text-xs text-muted-foreground">Calculating dates…</p>
              )}
              {recPreview && !recPreview.error && (
                <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3" data-testid="rec-preview">
                  <p className="text-sm font-medium text-emerald-500">
                    {recPreview.count} session{recPreview.count !== 1 ? "s" : ""}{recPreview.count > 1 ? ` — ${recPreview.summary}` : ""}
                  </p>
                  {recPreview.count > 1 && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">View all {recPreview.count} dates</summary>
                      <pre className="mt-2 text-[10px] font-mono max-h-48 overflow-y-auto whitespace-pre-wrap text-muted-foreground">{recPreview.table}</pre>
                    </details>
                  )}
                </div>
              )}
              {recPreview?.error && (
                <p className="text-xs text-rose-500" data-testid="rec-error">{recPreview.error}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Custom Terms (optional)</label>
              <textarea
                data-testid="contract-custom-terms"
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[60px]"
                placeholder="Any additional terms for this specific contract..."
                value={contractForm.custom_terms}
                onChange={(e) => setContractForm({ ...contractForm, custom_terms: e.target.value })}
              />
            </div>
            {contractPreview && (
              <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border border-border max-h-64 overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Contract Preview:</p>
                <pre className="text-[11px] sm:text-sm leading-relaxed font-sans" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }}>{contractPreview}</pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContract(false)}>Cancel</Button>
            <Button variant="outline" onClick={saveContractDraft} data-testid="save-contract-draft-btn" className="text-emerald-600 hover:text-emerald-500 border-emerald-500/40">
              <FileText size={14} /> Save Draft
            </Button>
            <Button variant="outline" onClick={previewContract} data-testid="preview-contract-btn">
              <Eye size={14} /> Preview
            </Button>
            <Button
              variant="outline"
              onClick={() => sendContract(true)}
              data-testid="save-without-sending-btn"
              className="text-amber-600 hover:text-amber-500 border-amber-500/40"
              title="Create the contract record and block the dates on the calendar, but do NOT email the client. Use when the booking was secured via another system."
            >
              <Save size={14} /> Save Without Sending
            </Button>
            <Button data-testid="send-contract-confirm" onClick={() => sendContract(false)}>
              <Send size={14} /> Send Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Addendum Dialog */}
      {/* Send Square Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Square Invoice</DialogTitle>
            <p className="text-xs text-muted-foreground">Branded invoice with line items, deposit + balance schedule, and ACH bank-transfer support. Auto-recorded to Finances when paid.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Title</label>
              <Input data-testid="inv-title" value={invForm.title} onChange={(e) => setInvForm({...invForm, title: e.target.value})} placeholder="Invoice title shown to client" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Description (optional)</label>
              <textarea
                data-testid="inv-description"
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[60px]"
                placeholder="Notes shown on the invoice"
                value={invForm.description}
                onChange={(e) => setInvForm({...invForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Line items</label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="inv-add-line">
                  <Plus size={12} /> Add line
                </Button>
              </div>
              <div className="space-y-2">
                {invForm.line_items.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-line-${idx}`}>
                    <Input className="col-span-7" placeholder="Description" value={li.description} onChange={(e) => updateLineItem(idx, "description", e.target.value)} />
                    <Input className="col-span-2" type="number" step="0.5" min="0" placeholder="Qty" value={li.qty} onChange={(e) => updateLineItem(idx, "qty", e.target.value)} />
                    <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="Price" value={li.unit_price} onChange={(e) => updateLineItem(idx, "unit_price", e.target.value)} />
                    <button type="button" onClick={() => removeLineItem(idx)} className="col-span-1 text-muted-foreground hover:text-rose-500 disabled:opacity-30" disabled={invForm.line_items.length === 1}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-right">Total: <span className="font-mono font-medium text-foreground">${(invoiceTotalCents/100).toFixed(2)}</span></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Deposit %</label>
                <Input type="number" min="0" max="100" step="5" value={invForm.deposit_pct} onChange={(e) => setInvForm({...invForm, deposit_pct: e.target.value})} data-testid="inv-deposit-pct" placeholder="0 for no deposit" />
                {invoiceTotalCents > 0 && parseFloat(invForm.deposit_pct) > 0 && (
                  <p className="text-[10px] text-muted-foreground">Deposit due today: ${((invoiceTotalCents * (parseFloat(invForm.deposit_pct) || 0)) / 100 / 100).toFixed(2)}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Balance due date</label>
                <Input type="date" value={invForm.balance_due_date} onChange={(e) => setInvForm({...invForm, balance_due_date: e.target.value})} data-testid="inv-balance-due" />
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" className="h-4 w-4" checked={invForm.allow_ach} onChange={(e) => setInvForm({...invForm, allow_ach: e.target.checked})} data-testid="inv-allow-ach" />
                <span>Accept ACH (bank transfer)</span>
                <span className="text-[10px] text-muted-foreground">— 1% fee, $10 cap</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" className="h-4 w-4" checked={invForm.auto_reminders} onChange={(e) => setInvForm({...invForm, auto_reminders: e.target.checked})} data-testid="inv-auto-reminders" />
                <span>Auto-reminders</span>
                <span className="text-[10px] text-muted-foreground">— 14d, 3d, day-of</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoice(false)}>Cancel</Button>
            <Button onClick={sendInvoice} disabled={invSending || invoiceTotalCents <= 0} data-testid="inv-send-confirm">
              <Send size={14} /> {invSending ? "Sending…" : `Send Invoice ($${(invoiceTotalCents/100).toFixed(2)})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddendum} onOpenChange={setShowAddendum}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Addendum</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Title</label>
              <Input data-testid="addendum-title" value={addendumForm.title} onChange={(e) => setAddendumForm({ ...addendumForm, title: e.target.value })} placeholder="e.g. Add-on: 5 Extra Guests + Pizza" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Change Event Date <span className="text-xs text-muted-foreground font-normal">(optional — leave blank if not changing)</span></label>
              <Input
                data-testid="addendum-new-date"
                type="date"
                value={addendumForm.new_event_date}
                onChange={(e) => setAddendumForm({ ...addendumForm, new_event_date: e.target.value })}
              />
              {addendumForm.new_event_date && (
                <p className="text-xs text-amber-600">
                  This will move the event from <b>{lead?.requested_date || "TBD"}</b> to <b>{addendumForm.new_event_date}</b> and update the lead.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Line Items</label>
              <div className="space-y-2">
                {addendumForm.line_items.map((line, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-6" data-testid={`addendum-desc-${i}`} placeholder="Description (e.g. Extra guests)" value={line.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                    <Input className="col-span-2" data-testid={`addendum-qty-${i}`} type="number" placeholder="Qty" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                    <Input className="col-span-3" data-testid={`addendum-price-${i}`} type="number" placeholder="Unit price" value={line.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(i)} disabled={addendumForm.line_items.length === 1}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="addendum-add-line">
                <Plus size={14} /> Add Line Item
              </Button>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notes (optional)</label>
              <textarea
                data-testid="addendum-notes"
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[60px]"
                placeholder="Additional context for the client..."
                value={addendumForm.notes}
                onChange={(e) => setAddendumForm({ ...addendumForm, notes: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
              <span className="text-sm text-muted-foreground">Addendum Total</span>
              <span className="text-2xl font-mono font-bold text-primary" data-testid="addendum-total">${addendumTotal.toFixed(2)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddendum(false)}>Cancel</Button>
            <Button variant="outline" onClick={previewAddendum} data-testid="addendum-preview-btn">
              <Eye size={14} /> Preview
            </Button>
            <Button onClick={sendAddendum} data-testid="addendum-send-btn">
              <Send size={14} /> Send Addendum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Addendum Preview Dialog */}
      <Dialog open={showAddendumPreview} onOpenChange={setShowAddendumPreview}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Addendum Preview</DialogTitle>
            <p className="text-xs text-muted-foreground">This is what the client will see in the email & sign page.</p>
          </DialogHeader>
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed bg-muted/30 border border-border rounded-md p-4 max-h-[60vh] overflow-y-auto" data-testid="addendum-preview-content">{addendumPreview}</pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddendumPreview(false)}>Close</Button>
            <Button onClick={() => { setShowAddendumPreview(false); sendAddendum(); }} data-testid="addendum-preview-send">
              <Send size={14} /> Looks Good — Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Amount</label>
                <Input data-testid="payment-amount" type="number" step="0.01" placeholder="0.00" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Method</label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Date Received</label>
              <Input data-testid="payment-date" type="date" value={paymentForm.received_at} onChange={(e) => setPaymentForm({ ...paymentForm, received_at: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
              <Input data-testid="payment-note" placeholder="e.g. Check #1234" value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
            <Button onClick={recordPayment} data-testid="payment-save">
              <Save size={14} /> Save Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Contract Dialog */}
      <Dialog open={!!viewContract} onOpenChange={() => setViewContract(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewContract?.template_name}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-3 text-sm text-muted-foreground mb-2">
            <Badge variant="outline">{viewContract?.status}</Badge>
            {viewContract?.total_amount && <span>Total: ${viewContract.total_amount}</span>}
            <span>Sent: {viewContract?.sent_at ? formatET(viewContract.sent_at) : ""}</span>
          </div>
          <div className="bg-muted/30 rounded-lg p-6 text-sm whitespace-pre-wrap leading-relaxed">
            {viewContract?.body}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer Dialog */}
      <Dialog open={showEmailer} onOpenChange={setShowEmailer}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Send Email to {lead?.first_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="email-subject" placeholder="Subject" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
            <Input data-testid="email-cc" placeholder="CC (comma separated)" value={emailForm.cc} onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })} />
            <textarea data-testid="email-body" className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[120px]" placeholder="Email body..." value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailer(false)}>Cancel</Button>
            <Button data-testid="email-send-btn" onClick={sendEmail}><Send size={14} /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passcode Dialog */}
      <Dialog open={!!passcodeDialog} onOpenChange={() => { setPasscodeDialog(null); setPasscode(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {passcodeDialog === "delete" ? "Delete Lead" : passcodeDialog === "cancel" ? "Cancel Booking" : passcodeDialog === "refund" ? "Refund Payment" : "Release Reservation"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {passcodeDialog === "refund" ? "This will initiate a refund. Enter passcode to confirm." : "Enter passcode to confirm this action."}
          </p>
          <Input
            data-testid="passcode-input"
            type="password"
            placeholder="Enter passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePasscodeAction()}
          />
          <DialogFooter>
            <Button data-testid="passcode-confirm-btn" onClick={handlePasscodeAction}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Booking Dialog */}
      <Dialog open={showNewBooking} onOpenChange={setShowNewBooking}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Booking for {lead?.first_name} {lead?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Contact info will be carried over. Pick the new booking type and date.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <Select value={newBookingForm.inquiry_type} onValueChange={(v) => setNewBookingForm({ ...newBookingForm, inquiry_type: v })}>
                  <SelectTrigger data-testid="nb-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rental">Rental</SelectItem>
                    <SelectItem value="birthday_party">Birthday Party</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="picnic">Picnic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Location</label>
                <Select value={newBookingForm.location} onValueChange={(v) => setNewBookingForm({ ...newBookingForm, location: v })}>
                  <SelectTrigger data-testid="nb-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rrdc_field">Rolling River Campus</SelectItem>
                    <SelectItem value="turf_house">Turf House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Date</label>
                <Input data-testid="nb-date" type="date" value={newBookingForm.requested_date} onChange={(e) => setNewBookingForm({ ...newBookingForm, requested_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Time</label>
                <Input data-testid="nb-time" type="time" value={newBookingForm.start_time} onChange={(e) => setNewBookingForm({ ...newBookingForm, start_time: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Duration (hours)</label>
                <Input data-testid="nb-duration" type="number" step="0.5" value={newBookingForm.duration} onChange={(e) => setNewBookingForm({ ...newBookingForm, duration: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewBooking(false)}>Cancel</Button>
            <Button onClick={createNewBooking} data-testid="nb-create"><Plus size={14} /> Create Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Info Dialog */}
      <Dialog open={showEditLead} onOpenChange={setShowEditLead}>
        <DialogContent className="max-w-lg w-[95vw]" data-testid="edit-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Edit Lead Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">First Name</label>
                <Input
                  data-testid="edit-lead-first-name"
                  value={editLeadForm.first_name}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Last Name</label>
                <Input
                  data-testid="edit-lead-last-name"
                  value={editLeadForm.last_name}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Organization</label>
              <Input
                data-testid="edit-lead-organization"
                placeholder="(optional)"
                value={editLeadForm.organization}
                onChange={(e) => setEditLeadForm({ ...editLeadForm, organization: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <Input
                  data-testid="edit-lead-email"
                  type="email"
                  value={editLeadForm.email}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Phone</label>
                <Input
                  data-testid="edit-lead-phone"
                  value={editLeadForm.phone}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Street</label>
              <Input
                data-testid="edit-lead-street"
                value={editLeadForm.street}
                onChange={(e) => setEditLeadForm({ ...editLeadForm, street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-medium text-muted-foreground">City</label>
                <Input
                  data-testid="edit-lead-city"
                  value={editLeadForm.city}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, city: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">State</label>
                <Input
                  data-testid="edit-lead-state"
                  value={editLeadForm.state}
                  onChange={(e) => setEditLeadForm({ ...editLeadForm, state: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">ZIP Code</label>
              <Input
                data-testid="edit-lead-zip"
                value={editLeadForm.zip_code}
                onChange={(e) => setEditLeadForm({ ...editLeadForm, zip_code: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLead(false)} data-testid="edit-lead-cancel">Cancel</Button>
            <Button onClick={saveLeadEdit} disabled={savingLeadEdit} data-testid="edit-lead-save">
              <Save size={14} /> {savingLeadEdit ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send / Resend Waiver Dialog */}
      <Dialog open={showWaiver} onOpenChange={setShowWaiver}>
        <DialogContent className="max-w-lg w-[95vw]" data-testid="waiver-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Send / Resend Waiver</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Waiver Template <span className="text-red-500">*</span></label>
              <Select value={waiverForm.waiver_id} onValueChange={(v) => setWaiverForm({ ...waiverForm, waiver_id: v })}>
                <SelectTrigger data-testid="waiver-template-select"><SelectValue placeholder="Select a waiver" /></SelectTrigger>
                <SelectContent>
                  {(templates || []).filter((t) => t.active && t.type === "waiver").map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground -mt-1">
              These fields appear at the <b>top and bottom</b> of the waiver. The organization signs on behalf of all participants — no individual player name. The email here is also where the signed copy comes back.
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Organization / Team Name <span className="text-red-500">*</span></label>
              <Input
                data-testid="waiver-organization"
                value={waiverForm.renter_organization}
                onChange={(e) => setWaiverForm({ ...waiverForm, renter_organization: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Email <span className="text-red-500">*</span></label>
                <Input
                  data-testid="waiver-email"
                  type="email"
                  value={waiverForm.renter_email}
                  onChange={(e) => setWaiverForm({ ...waiverForm, renter_email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Phone</label>
                <Input
                  data-testid="waiver-phone"
                  value={waiverForm.renter_phone}
                  onChange={(e) => setWaiverForm({ ...waiverForm, renter_phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Address</label>
              <Input
                data-testid="waiver-address"
                value={waiverForm.renter_address}
                onChange={(e) => setWaiverForm({ ...waiverForm, renter_address: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 pt-2 cursor-pointer text-xs text-muted-foreground">
              <input
                data-testid="waiver-update-lead"
                type="checkbox"
                checked={waiverForm.update_lead}
                onChange={(e) => setWaiverForm({ ...waiverForm, update_lead: e.target.checked })}
              />
              Also save these details back to the lead record
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWaiver(false)} data-testid="waiver-cancel">Cancel</Button>
            <Button onClick={sendWaiver} disabled={sendingWaiver} data-testid="waiver-send">
              <Send size={14} /> {sendingWaiver ? "Sending..." : "Send Waiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingHistoryRow({ bk, active, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAuditEntries] = useState(null);
  const date = bk.requested_date || "";
  const itype = (bk.inquiry_type || "").replace("_", " ");
  const status = (bk.status || "new").replace("_", " ");
  const docs = bk._contracts || [];
  const signed = docs.filter((c) => c.status === "signed");

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/contracts/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc.template_name || "document").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const viewDoc = async (doc) => {
    try {
      const res = await api.get(`/contracts/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      // Browsers will free the URL when the tab closes
    } catch {
      toast.error("Could not open PDF");
    }
  };

  const toggleAudit = async () => {
    if (!auditOpen && auditEntries === null) {
      try {
        const { data } = await api.get(`/leads/${bk.id}/audit`);
        setAuditEntries(data.entries || []);
      } catch {
        setAuditEntries([]);
      }
    }
    setAuditOpen((v) => !v);
  };

  return (
    <div className={`rounded-md border ${active ? "border-primary bg-primary/10" : "border-border bg-background"}`} data-testid={`booking-${bk.id}`}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors rounded-md"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium capitalize truncate">{itype}{date ? ` · ${date}` : ""}</p>
            <p className="text-[11px] text-muted-foreground capitalize">{status}{signed.length ? ` · ${signed.length} signed doc${signed.length > 1 ? "s" : ""}` : ""}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {active && <Badge variant="outline" className="text-[10px]">current</Badge>}
            <ChevronDown size={14} className={`text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-border">
          {docs.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic">No documents on this booking</p>
          ) : (
            docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 py-1.5 text-xs">
                <FileText size={12} className={d.is_waiver ? "text-amber-500" : d.is_addendum ? "text-purple-500" : "text-primary"} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{d.template_name}{d.is_addendum && d.total_amount ? ` — $${parseFloat(d.total_amount).toFixed(2)}` : ""}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">
                    {d.is_waiver ? "Waiver" : d.is_addendum ? "Addendum" : "Contract"} · {d.status}{d.signed_at ? ` · ${formatDateET(d.signed_at)}` : ""}
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); viewDoc(d); }} data-testid={`bk-doc-view-${d.id}`} title="View PDF">
                  <Eye size={11} />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); downloadDoc(d); }} data-testid={`bk-doc-download-${d.id}`} title="Download PDF">
                  <Download size={11} />
                </Button>
              </div>
            ))
          )}

          {/* Audit Trail expandable */}
          <div className="pt-1 border-t border-border">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleAudit(); }}
              className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
              data-testid={`bk-audit-toggle-${bk.id}`}
            >
              <span className="flex items-center gap-1.5">
                <ClipboardCheck size={12} />
                <span>Action Updates</span>
              </span>
              <ChevronDown size={12} className={`transition-transform ${auditOpen ? "rotate-180" : ""}`} />
            </button>
            {auditOpen && (
              <div className="space-y-1 pt-1.5">
                {auditEntries === null ? (
                  <p className="text-[11px] text-muted-foreground italic">Loading…</p>
                ) : auditEntries.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic">No actions logged for this booking</p>
                ) : (
                  auditEntries.map((a) => (
                    <div key={a.id} className="text-[11px] py-0.5">
                      <span className="text-muted-foreground">{formatET(a.timestamp)}</span>
                      <span className="mx-1">·</span>
                      <span className="font-medium capitalize">{(a.action || "").replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground"> by {a.username}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {!active && (
            <Button variant="outline" size="sm" className="w-full h-7 text-xs mt-1" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`bk-open-${bk.id}`}>
              Open Booking
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, v, bold }) {
  const text = `$${(v || 0).toFixed(2)}`;
  return (
    <div className={`flex items-baseline justify-between ${bold ? "font-semibold text-amber-900 dark:text-amber-100" : "text-amber-800/90 dark:text-amber-200/80"}`}>
      <span>{label}</span>
      <span className="font-mono">{text}</span>
    </div>
  );
}

